// Inline record editors for the Cleanup workspace (Donna's request).
//
// When a steward is deciding a cleanup issue and just needs to correct
// the record — fix an ALL-CAPS name, add a missing email, fix an
// address — she shouldn't have to leave Cleanup, open the full record,
// and hunt for "Edit details". These editors let her fix the core
// fields in place, right where the record's details are shown (the
// research drawer), and save through the SAME PATCH endpoints the full
// record page uses (PATCH /api/contacts/:id, PATCH /api/households/:id).
//
// Scope is deliberately narrow — the fields a front-desk steward
// actually corrects (see cleanup-edit.ts). Sensitive fields (SSN / DL /
// bank) are excluded by construction and never appear here.
//
// NOTE: household edits depend on the edit-permission fix
// (fix/ryan-unlock-contact-household-edit) so a front-desk save
// succeeds — before that, PATCH /api/households/:id is admin-gated and a
// non-admin save returns 403 (surfaced with a plain-language message, no
// dead end).

import { useMemo, useState } from "react";

import {
  api,
  ApiError,
  type Household,
  type SafeContact,
} from "../api";
import { Button } from "../ui";
import { formatPhoneInput } from "../pages/contact-page-helpers";
import {
  computeEditPatch,
  contactDraftFrom,
  householdDraftFrom,
  type ContactCoreField,
  type HouseholdAddressField,
} from "./cleanup-edit";
import { DateField } from "../components/DateField";

// What the drawer host learns after a save completes: which kind of
// record was edited, and whether anything actually changed (a no-op save
// where the draft equalled the record). The host uses this to show
// honest save feedback (see cleanup-toast.ts).
export type InlineSaveResult = {
  entity: "contact" | "household";
  changed: boolean;
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 10.5,
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "var(--blue-dark)",
  marginBottom: 4,
};
const inputStyle: React.CSSProperties = {
  width: "100%",
  border: "1px solid var(--mid-gray)",
  borderRadius: 8,
  padding: "8px 10px",
  fontSize: 13.5,
  color: "var(--navy)",
  background: "var(--bg-0)",
  font: "inherit",
  boxSizing: "border-box",
};
const groupHeadStyle: React.CSSProperties = {
  fontSize: 10.5,
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: "var(--fg-muted)",
  margin: "16px 0 8px",
};

function EditField({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  autoFocus,
  onBlur,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  autoFocus?: boolean;
  onBlur?: (v: string) => void;
}) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={labelStyle}>{label}</label>
      {type === "date" ? (
        <DateField value={value} autoFocus={autoFocus} onChange={onChange} style={inputStyle} />
      ) : (
        <input
          type={type}
          value={value}
          placeholder={placeholder}
          autoFocus={autoFocus}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur ? (e) => onBlur(e.target.value) : undefined}
          style={inputStyle}
        />
      )}
    </div>
  );
}

// Turn a save error into plain language. The dependency (front-desk edit
// unlock) means a household save can 403 until that ships — say so
// clearly rather than showing a raw status.
function saveErrorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 403) {
      return "You don't have permission to edit this record yet. Nothing was changed.";
    }
    if (e.status === 422) {
      return `${e.message} — nothing was changed.`;
    }
    return e.message;
  }
  return "Couldn't save the changes. Nothing was changed.";
}

export function ContactInlineEdit({
  contact,
  onCancel,
  onSaved,
}: {
  contact: SafeContact;
  onCancel: () => void;
  // Called after a successful save (or a no-op "nothing changed"). The
  // result tells the host what to show as feedback.
  onSaved: (result: InlineSaveResult) => void;
}) {
  const original = useMemo(() => contactDraftFrom(contact), [contact]);
  const [draft, setDraft] = useState(original);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function set(field: ContactCoreField, value: string) {
    setDraft((d) => ({ ...d, [field]: value }));
    if (error) setError(null);
  }

  async function save() {
    const patch = computeEditPatch(original, draft);
    const count = Object.keys(patch).length;
    if (count === 0) {
      onSaved({ entity: "contact", changed: false });
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.updateContact(contact.id, patch);
      onSaved({ entity: "contact", changed: true });
    } catch (e) {
      setError(saveErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div style={{ ...groupHeadStyle, marginTop: 0 }}>Name</div>
      <EditField
        label="Legal first name"
        value={draft.legal_first_name}
        onChange={(v) => set("legal_first_name", v)}
        autoFocus
      />
      <EditField
        label="Legal last name"
        value={draft.legal_last_name}
        onChange={(v) => set("legal_last_name", v)}
      />
      <EditField
        label="Preferred name"
        value={draft.preferred_name}
        placeholder='e.g. "Bob" for "Robert"'
        onChange={(v) => set("preferred_name", v)}
      />

      <div style={groupHeadStyle}>Contact info</div>
      <EditField
        label="Primary email"
        type="email"
        value={draft.email}
        onChange={(v) => set("email", v)}
      />
      <EditField
        label="Primary phone"
        value={draft.phone_primary}
        onChange={(v) => set("phone_primary", v)}
        onBlur={(v) => set("phone_primary", formatPhoneInput(v))}
      />

      <div style={groupHeadStyle}>Mailing address</div>
      <EditField
        label="Street"
        value={draft.street_line_1}
        onChange={(v) => set("street_line_1", v)}
      />
      <EditField
        label="Apt / Unit"
        value={draft.street_line_2}
        onChange={(v) => set("street_line_2", v)}
      />
      <EditField
        label="City"
        value={draft.city}
        onChange={(v) => set("city", v)}
      />
      <EditField
        label="State"
        value={draft.state}
        placeholder="Two-letter, e.g. AZ"
        onChange={(v) => set("state", v)}
      />
      <EditField
        label="ZIP"
        value={draft.zip}
        onChange={(v) => set("zip", v)}
      />

      {error && (
        <div
          style={{
            marginTop: 4,
            marginBottom: 10,
            color: "var(--danger)",
            fontSize: 12.5,
            lineHeight: 1.45,
          }}
        >
          {error}
        </div>
      )}

      <EditActions saving={saving} onSave={save} onCancel={onCancel} />
    </div>
  );
}

export function HouseholdInlineEdit({
  household,
  onCancel,
  onSaved,
}: {
  household: Household;
  onCancel: () => void;
  onSaved: (result: InlineSaveResult) => void;
}) {
  const original = useMemo(() => householdDraftFrom(household), [household]);
  const [draft, setDraft] = useState(original);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function set(field: HouseholdAddressField, value: string) {
    setDraft((d) => ({ ...d, [field]: value }));
    if (error) setError(null);
  }

  async function save() {
    const patch = computeEditPatch(original, draft);
    const count = Object.keys(patch).length;
    if (count === 0) {
      onSaved({ entity: "household", changed: false });
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.updateHousehold(household.id, patch);
      onSaved({ entity: "household", changed: true });
    } catch (e) {
      setError(saveErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div style={{ ...groupHeadStyle, marginTop: 0 }}>Household address</div>
      <EditField
        label="Street"
        value={draft.primary_street_line_1}
        onChange={(v) => set("primary_street_line_1", v)}
        autoFocus
      />
      <EditField
        label="Apt / Unit"
        value={draft.primary_street_line_2}
        onChange={(v) => set("primary_street_line_2", v)}
      />
      <EditField
        label="City"
        value={draft.primary_city}
        onChange={(v) => set("primary_city", v)}
      />
      <EditField
        label="State"
        value={draft.primary_state}
        placeholder="Two-letter, e.g. AZ"
        onChange={(v) => set("primary_state", v)}
      />
      <EditField
        label="ZIP"
        value={draft.primary_zip}
        onChange={(v) => set("primary_zip", v)}
      />
      <div
        style={{
          fontSize: 11.5,
          color: "var(--fg-muted)",
          lineHeight: 1.45,
          marginBottom: 10,
        }}
      >
        To fix a member's name, email, or phone, open that person's record
        and edit the contact.
      </div>

      {error && (
        <div
          style={{
            marginTop: 4,
            marginBottom: 10,
            color: "var(--danger)",
            fontSize: 12.5,
            lineHeight: 1.45,
          }}
        >
          {error}
        </div>
      )}

      <EditActions saving={saving} onSave={save} onCancel={onCancel} />
    </div>
  );
}

function EditActions({
  saving,
  onSave,
  onCancel,
}: {
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <div style={{ display: "flex", gap: 8 }}>
      <Button variant="primary" onClick={onSave} disabled={saving}>
        {saving ? "Saving…" : "Save changes"}
      </Button>
      <Button variant="ghost" onClick={onCancel} disabled={saving}>
        Cancel
      </Button>
    </div>
  );
}
